/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Users, GitBranch, Sparkles, Download, CheckCircle2, Shield, BookOpen, Network } from 'lucide-react';
import { Button } from '../../components/ui/button';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Zap className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-slate-900">Velocity</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">
                Features
              </a>
              <Link to="/home/download" className="text-slate-600 hover:text-slate-900 transition-colors">
                Download
              </Link>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">
                Pricing
              </a>
              <Link to="/home/changelog" className="text-slate-600 hover:text-slate-900 transition-colors">
                Changelog
              </Link>
              <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">
                Launch App
              </Link>
              <Link to="/signup">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              Ship product{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                at the speed of thought
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-10 leading-relaxed">
              Velocity is the operating system for the Agentic SDLC—orchestrating AI agents across your entire product lifecycle from spec to ship.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/home/download">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6">
                  <Download className="mr-2 h-5 w-5" />
                  Download for macOS
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  Try Web Version
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Also available for Windows and Linux
            </p>
          </div>

          {/* Hero Image/Demo */}
          <div className="mt-20 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl" />
            <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-900 px-4 py-3 flex items-center space-x-2">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <span className="text-slate-400 text-sm ml-4">Velocity PPM</span>
              </div>
              <div className="bg-slate-50">
                <img 
                  src="/product/assets/velocity_screen.png" 
                  alt="Velocity PPM Interface" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Everything you need to ship faster
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Velocity combines AI agents, spec-driven development, and seamless integrations to accelerate your product development.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-8 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">AI-Powered Development</h3>
              <p className="text-slate-600 leading-relaxed">
                Harness AI agents to implement features, fix bugs, and groom stories. Watch as AI transforms your product backlog into working code.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-8 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                <GitBranch className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Spec-Driven Development</h3>
              <p className="text-slate-600 leading-relaxed">
                Track epics, stories, and issues in markdown for perfect AI context and version control. Your specs become your source of truth.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-8 rounded-xl bg-gradient-to-br from-green-50 to-green-100/50 border border-green-200">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Seamless Integrations</h3>
              <p className="text-slate-600 leading-relaxed">
                Connect with JIRA, Linear, and GitHub. Import your existing work and keep everything in sync across your tools.
              </p>
            </div>
          </div>

          {/* Enterprise Features - VPS and DSA */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Enterprise-Grade AI Orchestration</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* VPS */}
              <div className="p-8 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100/50 border border-indigo-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Velocity Playbook System</h3>
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Automated context engineering for AI agents. The VPS creates and maintains the organizational knowledge, standards, and strategic guidance that coding agents need to deliver aligned output.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Context Library with semantic search for playbooks, skills, and MCP servers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Auto-generated AGENTS.md files for Cursor, Claude Code, and other coding tools</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Progressive context delivery—minimal tokens, maximum signal</span>
                  </li>
                </ul>
              </div>

              {/* DSA */}
              <div className="p-8 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center">
                    <Network className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Distributed System Agents</h3>
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Organization-level AI oversight across your entire product portfolio—without central infrastructure. DSAs leverage Git for distributed coordination and use spare compute across your team.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Security audits, coverage analysis, and architecture reviews across all products</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Zero infrastructure overhead—no dedicated server required</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Fully auditable operations recorded in Git history</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Additional Features */}
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {[
              'Real-time AI agent execution with tool visibility',
              'MCP server for Cursor, Claude Code, and IDE integration',
              'Multi-agent workflows for complex tasks',
              'Comprehensive product portfolio management',
              'Story and issue grooming with AI',
              'Automated security, coverage, and architecture audits',
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Get Velocity
            </h2>
            <p className="text-xl text-slate-600">
              Download the desktop app or use the web version
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* macOS */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">macOS</h3>
                <p className="text-sm text-slate-600 mb-4">Apple Silicon & Intel</p>
                <Link to="/home/download">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Download .dmg
                  </Button>
                </Link>
              </div>
            </div>

            {/* Windows */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Windows</h3>
                <p className="text-sm text-slate-600 mb-4">Windows 10+</p>
                <Link to="/home/download">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Download .exe
                  </Button>
                </Link>
              </div>
            </div>

            {/* Linux */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Linux</h3>
                <p className="text-sm text-slate-600 mb-4">AppImage</p>
                <Link to="/home/download">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Download
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Or try the web version</p>
            <Link to="/signup">
              <Button size="lg" variant="outline">
                Launch Web App
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-slate-600">
              Start free, upgrade when you need more
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Tier */}
            <div className="bg-white rounded-xl p-8 border-2 border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Free</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">$0</span>
                <span className="text-slate-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">1 product</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Basic AI features</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Community support</span>
                </li>
              </ul>
              <Link to="/signup" className="block">
                <Button variant="outline" className="w-full">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="bg-blue-600 rounded-xl p-8 border-2 border-blue-600 relative transform scale-105">
              <div className="absolute top-0 right-6 -translate-y-1/2">
                <span className="bg-yellow-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                  POPULAR
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$29</span>
                <span className="text-blue-100">/user/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-200 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Unlimited products</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-200 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Advanced AI agents</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-200 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Priority support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-200 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">JIRA & Linear sync</span>
                </li>
              </ul>
              <Link to="/signup" className="block">
                <Button className="w-full bg-white text-blue-600 hover:bg-blue-50">
                  Start Free Trial
                </Button>
              </Link>
            </div>

            {/* Enterprise Tier */}
            <div className="bg-white rounded-xl p-8 border-2 border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Enterprise</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">Custom</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Everything in Pro</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Velocity Playbook System</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Distributed System Agents</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">On-premise deployment</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full">
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to ship at the speed of thought?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join teams using Velocity to orchestrate AI agents across their entire product lifecycle
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/home/download">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                <Download className="mr-2 h-5 w-5" />
                Download Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Zap className="h-6 w-6 text-blue-500" />
                <span className="text-xl font-bold text-white">Velocity</span>
              </div>
              <p className="text-sm">
                The operating system for the Agentic SDLC. Orchestrate AI agents from spec to ship.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/home/download" className="hover:text-white transition-colors">Download</Link></li>
                <li><Link to="/home/changelog" className="hover:text-white transition-colors">Changelog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-sm text-center">
            <p>&copy; 2025 Lumavec Group. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

